import { Suspense, use, useCallback, useState, type ReactNode } from 'react';
import { getLocalProfile, kvSet, putLocalProfile } from '@/data/db';
import { enqueue } from '@/data/sync-queue';
import type { Profile } from '@/data/types';
import { SessionContext } from './session-context';
import {
  invalidateSessionResource,
  loadSession,
  sessionResource,
  ONBOARDING_KEY,
  type BootData,
} from './session-resource';
import { ErrorBoundary } from './ErrorBoundary';
import { Button } from '@/components';
import { t } from '@/i18n';

function BootSpinner() {
  return (
    <div className="grid min-h-dvh place-items-center bg-bg" role="status" aria-live="polite">
      <span className="sr-only">{t('common.loading')}</span>
      <span
        aria-hidden
        className="size-10 animate-spin rounded-full border-4 border-line border-t-brand"
      />
    </div>
  );
}

function BootError({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="grid min-h-dvh place-items-center gap-4 bg-bg p-6 text-center">
      <div>
        <h1 className="mb-2 text-2xl">{t('error.title')}</h1>
        <p className="mb-4 text-text-2">{error.message}</p>
        <Button
          onClick={() => {
            invalidateSessionResource();
            reset();
          }}
        >
          {t('common.retry')}
        </Button>
      </div>
    </div>
  );
}

function SessionBoot({ children }: { children: ReactNode }) {
  // Suspends until the session and profile are ready. No effect involved, so the
  // first paint is the real app rather than a flash of empty state.
  const initial = use(sessionResource());
  const [data, setData] = useState<BootData>(initial);

  const completeOnboarding = useCallback(async () => {
    await kvSet(ONBOARDING_KEY, true);
    setData((d) => ({ ...d, onboardingComplete: true }));
  }, []);

  const updateProfile = useCallback(async (patch: Partial<Profile>) => {
    // Merge against the stored row rather than component state: IndexedDB is the
    // local source of truth and may have been written by another tab.
    const current = await getLocalProfile();
    if (current === undefined) return;
    const next: Profile = { ...current, ...patch, updatedAt: new Date().toISOString() };
    await putLocalProfile(next);
    // Settings are client-authoritative, so they go through the ordinary queue.
    await enqueue('settings', next);
    setData((d) => ({ ...d, profile: next }));
  }, []);

  const refresh = useCallback(async () => {
    invalidateSessionResource();
    setData(await loadSession());
  }, []);

  return (
    <SessionContext.Provider
      value={{
        session: data.session,
        profile: data.profile,
        onboardingComplete: data.onboardingComplete,
        completeOnboarding,
        updateProfile,
        refresh,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

/**
 * Creates (or restores) the session before the app renders. Anonymous auth means
 * the very first lesson is already being saved, which is the plan's
 * try-before-signup requirement.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary fallback={(error, reset) => <BootError error={error} reset={reset} />}>
      <Suspense fallback={<BootSpinner />}>
        <SessionBoot>{children}</SessionBoot>
      </Suspense>
    </ErrorBoundary>
  );
}
